import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Container, Input } from 'components/atoms';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { t } from 'i18next';
import { PiChartBar, PiGlobe } from 'react-icons/pi';
import { useGetSpotifyStatistics, ISpotifyStatisticsQuery } from 'services/statistics';
import { useGetSettlementListInfinite } from 'services/settlementPeriods';
import { useGetTrackLightList } from 'services/tracks/light';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { formatDateToInput } from 'utils/global/date.utils';
import SpotifyChartContainer from 'components/molecules/statistics/SpotifyChartContainer';
import StatisticsListContainer from 'components/molecules/statistics/StatisticsListContainer';

const SpotifyStatistics: React.FC = () => {
  usePermissionGuard("StatisticsPermissionsLIST**");

  const [statisticsQuery, setStatisticsQuery] = useState<ISpotifyStatisticsQuery>({
    startDate: '',
    endDate: '',
    country: '',
    deviceType: '',
    oS: '',
    gender: '',
    ageRange: '',
    trackIsrcs: [],
    EAN: '',
    accountId: '',
  });

  const spotifyStats = useGetSpotifyStatistics(statisticsQuery, true);
  
  const totalStreams = useMemo(() => {
    if (!spotifyStats.data?.statistics) return 0;
    return spotifyStats.data.statistics.reduce((sum, stat) => sum + stat.totalStreams, 0);
  }, [spotifyStats.data]);
  
  const settlementPeriods = useGetSettlementListInfinite({});
  
  const tracksLightList = useGetTrackLightList();

  const countryData = useMemo(() => {
    if (!spotifyStats.data?.statistics) {
      return [];
    }
    
    const allCountryStreams: { [key: string]: number } = {};
    
    spotifyStats.data.statistics.forEach(stat => {
      if (stat.streamsByCountry) {
        Object.entries(stat.streamsByCountry).forEach(([country, count]) => {
          allCountryStreams[country] = (allCountryStreams[country] || 0) + count;
        });
      }
    });
    
    const entries = Object.entries(allCountryStreams);
    const total = entries.reduce((sum, [, value]) => sum + value, 0);
    
    const data = entries
      .map(([code, value]) => ({ 
        name: t(`countries.${code.toUpperCase()}`), 
        value,
        percentage: total > 0 ? Math.round((value / total) * 100) : 0
      }))
      .sort((a, b) => b.value - a.value);
    return data;
  }, [spotifyStats.data]);

  const streamsData = useMemo(() => {
    if (!spotifyStats.data?.statistics) return [];
    
    return spotifyStats.data.statistics.map(stat => ({
      name: new Date(stat.date).toISOString().split('T')[0],
      value: stat.totalStreams,
    }));
  }, [spotifyStats.data]);

  const genderData = useMemo(() => {
    if (!spotifyStats.data?.statistics) return [];
    
    const allGenderStreams: { [key: string]: number } = {};
    
    spotifyStats.data.statistics.forEach(stat => {
      if (stat.streamsByGender) {
        Object.entries(stat.streamsByGender).forEach(([gender, count]) => {
          allGenderStreams[gender] = (allGenderStreams[gender] || 0) + count;
        });
      }
    });
    
    const colors = ['#FF6B9D', '#4ECDC4', '#45B7D1', '#96CEB4'];
    return Object.entries(allGenderStreams).map(([key, value], index) => ({
      name: t(`navbar.gender.${key}`),
      value,
      color: colors[index] || '#CCCCCC'
    }));
  }, [spotifyStats.data]);

  const ageData = useMemo(() => {
    if (!spotifyStats.data?.statistics) return [];
    
    const allAgeGroups: { [key: string]: number } = {};
    
    spotifyStats.data.statistics.forEach(stat => {
      if (stat.streamsByGenderAge) {
        Object.values(stat.streamsByGenderAge).forEach(genderData => {
          Object.entries(genderData).forEach(([ageGroup, count]) => {
            allAgeGroups[ageGroup] = (allAgeGroups[ageGroup] || 0) + count;
          });
        });
      }
    });
    
    return Object.entries(allAgeGroups).map(([name, value]) => ({ name, value }));
  }, [spotifyStats.data]);


  const dateRangeOptions = useMemo(() => [
    { label: t('navbar.last24h', 'Ostatnie 24h'), value: '24h' },
    { label: t('navbar.last7days', 'Ostatnie 7 dni'), value: '7d' },
    { label: t('navbar.last30days', 'Ostatnie 30 dni'), value: '30d' },
    { label: t('navbar.selectMonth', 'Wybierz miesiąc'), value: 'month' },
  ], []);

  const settlementPeriodOptions = useMemo(() => 
    settlementPeriods.data?.pages?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName) || period.settlementPeriodName.name,
      value: period.id,
    })) ?? []
  , [settlementPeriods.data]);

  const trackOptions = useMemo(() => 
    tracksLightList.data?.map(track => ({
      label: track.mainTitle || 'unknown name',
      value: (track as any).isrc || track.id,
    })) ?? []
  , [tracksLightList.data]);

  const [selectedDateRange, setSelectedDateRange] = useState<string>('30d');
  const [selectedSettlementPeriod, setSelectedSettlementPeriod] = useState<string>('');
  const [selectedTrack, setSelectedTrack] = useState<string>('');

  const getDateRange = useCallback((range: string, settlementPeriodId?: string) => {
    if (range === 'month' && settlementPeriodId) {
      const selectedPeriod = settlementPeriods.data?.pages?.flat().find(p => p.id === settlementPeriodId);
      if (selectedPeriod) {
        const startDate = new Date(selectedPeriod.startDate);
        const endDate = new Date(selectedPeriod.endDate);
        
        return {
          startDate: formatDateToInput(startDate, true),
          endDate: formatDateToInput(endDate, true),
        };
      }
    }

    const now = new Date();
    const endDate = new Date(now);
    let startDate = new Date(now);

    switch (range) {
      case '24h':
        startDate.setHours(now.getHours() - 24);
        break;
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'month':
        startDate.setDate(now.getDate() - 30);
        break;
      default:
        startDate.setDate(now.getDate() - 30);
    }

    const formatDate = (date: Date, isEndDate: boolean = false) => {
      if (isEndDate) {
        const endDate = new Date(date);
        endDate.setHours(23, 59, 59, 999);
        return formatDateToInput(endDate, true);
      } else {
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);
        return formatDateToInput(startDate, true);
      }
    };

    return {
      startDate: formatDate(startDate, false),
      endDate: formatDate(endDate, true),
    };
  }, [settlementPeriods.data]);

  useEffect(() => {
    const { startDate, endDate } = getDateRange(selectedDateRange, selectedSettlementPeriod);
    setStatisticsQuery(prev => ({
      ...prev,
      startDate,
      endDate,
      trackIsrcs: selectedTrack ? [selectedTrack] : [],
    }));
  }, [selectedDateRange, selectedSettlementPeriod, selectedTrack, getDateRange]);


  return (
    <div className="flex flex-col gap-4 w-full" style={{ width: '100%', maxWidth: '100%', minWidth: '100%' }}>
       <Container className="w-full" style={{ width: '100%', maxWidth: '100%', minWidth: '100%' }}>
         <Container.Header
           title={
             <>
               <PiChartBar /> {t('statistics.spotify.title', 'Statystyki wydanych treści')}
             </>
           }
         >
           <div className="info-box-items flex flex-col sm:flex-row p-4 w-full gap-4">
             <Input.ComplexWrapper className="min-w-48">
               <Input.Select
                 label={t('input.label.dateRange', 'Zakres dat')}
                 value={dateRangeOptions.find(opt => opt.value === selectedDateRange) || dateRangeOptions[2]}
                 onChange={(option) => {
                   setSelectedDateRange((option?.value as string) || '30d');
                   if (option?.value !== 'month') {
                     setSelectedSettlementPeriod('');
                   }
                 }}
                 options={dateRangeOptions}
                 isClearable={false}
               />
             </Input.ComplexWrapper>
             {selectedDateRange === 'month' && (
               <Input.ComplexWrapper className="min-w-48">
                 <Input.Select
                   label={t('input.label.settlementPeriod', 'Okres rozliczeniowy')}
                   value={settlementPeriodOptions.find(opt => opt.value === selectedSettlementPeriod) || undefined}
                   onChange={(option) => {
                     setSelectedSettlementPeriod(option?.value as string || '');
                   }}
                   placeholder={t('input.placeholder.select-default', 'Wybierz...')}
                   options={settlementPeriodOptions}
                   isClearable={false}
                 />
               </Input.ComplexWrapper>
             )}
             <Input.ComplexWrapper className="min-w-48">
               <Input.Select
                 label={t('input.label.track', 'Utwór')}
                 value={trackOptions.find(opt => opt.value === selectedTrack) || undefined}
                 onChange={(option) => {
                   setSelectedTrack(option?.value as string || '');
                 }}
                 placeholder={t('input.placeholder.select-default', 'Wybierz...')}
                 options={trackOptions}
                 isClearable={true}
                 isSearchable={true}
                 isLoading={tracksLightList.isLoading}
               />
             </Input.ComplexWrapper>
           </div>
         </Container.Header>
       </Container>

      <Container className="w-full" style={{ width: '100%', maxWidth: '100%', minWidth: '100%' }}>
        <Container.Header
          title={
            <>
              <PiChartBar /> {t('statistics.spotify.totalStreams', 'Łączna liczba odtworzeń')}
            </>
          }
        >
           <div className="info-box-items flex p-4 w-full">
             <div className="flex items-center justify-center">
               <div className="text-center">
                 <div
                   className="text-[18px] font-bold mr-4"
                   style={{ color: 'var(--text)' }}>
                   {totalStreams.toLocaleString()}
                 </div>
               </div>
             </div>
           </div>
        </Container.Header>
      </Container>

      <div className="flex flex-col gap-4 w-full">
        <div className="flex flex-col xl:flex-row gap-4 w-full">
          <div className="w-full xl:w-1/3">
            <StatisticsListContainer
              headerTitle={
                <>
                  <PiGlobe /> {t('statistics.spotify.countries', 'KRAJE')}
                </>
              }
              items={countryData}
              label={(item) => item.name}
              value={(item) => `${item.percentage}%`}
              loading={spotifyStats.isLoading}
              classname="h-96 overflow-y-auto"
            />
          </div>

          <div className="w-full xl:w-2/3">
            <SpotifyChartContainer
              title={t('statistics.spotify.streamsOverTime', 'WYŚWIETLEŃ')}
              data={streamsData}
              chartType="line"
              height={313}
              color="var(--primary)"
              showGrid={true}
              showLegend={false}
              formatValue={(value) => value.toLocaleString()}
            />
          </div>
        </div>

        <div className="flex flex-col xl:flex-row gap-4 w-full">
          <div className="w-full xl:w-1/3">
            <SpotifyChartContainer
              title={t('statistics.spotify.gender', 'Płeć')}
              data={genderData}
              chartType="donut"
              height={300}
              innerRadius={60}
              outerRadius={100}
              showLegend={true}
              showTooltip={true}
              formatValue={(value) => value.toLocaleString()}
            />
          </div>

          <div className="w-full xl:w-2/3">
            <SpotifyChartContainer
              title={t('statistics.spotify.age', 'Wiek')}
              data={ageData}
              chartType="bar"
              height={300}
              color="var(--primary)"
              showGrid={true}
              showLegend={false}
              formatValue={(value) => value.toLocaleString()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotifyStatistics;
